import React, { useState, useEffect, useCallback } from 'react';
import { AlertCircle, Eye, RefreshCw, Calculator, Plus, Trash2 } from 'lucide-react';
import Button from '../ui/Button';
import DetailsMappingSection from './DetailsMappingSection';
import ComputedFieldBuilder from './ComputedFieldBuilder';
import toast from 'react-hot-toast';
import { integrationsApi } from '../../services/integrations-api';
import { handleApiError } from '../../services/api';
import type {
  WebhookColumnMappingInput,
  SaveWebhookMappingRequest,
  FieldDataType,
  ComputedFieldConfig,
  TransformConfig,
} from '../../services/integrations-api';

interface PreviewData {
  transaction: Record<string, unknown>;
  details: Record<string, unknown>[];
  hasDetails: boolean;
  itemCount: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const COLUMN_GROUPS = [
  { label: 'Measures', prefix: 'measure_', slots: Array.from({ length: 8 }, (_, i) => `measure_${i + 1}`) },
  { label: 'Names', prefix: 'name_', slots: Array.from({ length: 3 }, (_, i) => `name_${i + 1}`) },
  { label: 'Labels', prefix: 'label_', slots: Array.from({ length: 9 }, (_, i) => `label_${i + 1}`) },
  { label: 'References', prefix: 'ref_', slots: Array.from({ length: 8 }, (_, i) => `ref_${i + 1}`) },
  { label: 'Dates', prefix: 'date_', slots: Array.from({ length: 3 }, (_, i) => `date_${i + 1}`) },
  { label: 'Flags', prefix: 'flag_', slots: Array.from({ length: 4 }, (_, i) => `flag_${i + 1}`) },
  { label: 'Notes', prefix: 'note_', slots: Array.from({ length: 3 }, (_, i) => `note_${i + 1}`) },
];

const DATA_TYPES: { value: FieldDataType; label: string }[] = [
  { value: 'currency', label: 'Currency' },
  { value: 'count', label: 'Count' },
  { value: 'percentage', label: 'Percentage' },
  { value: 'name', label: 'Name' },
  { value: 'category', label: 'Category' },
  { value: 'reference', label: 'Reference' },
  { value: 'identifier', label: 'Identifier' },
  { value: 'text', label: 'Text' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'date', label: 'Date' },
];

const TRANSFORM_OPTIONS: { value: string; label: string }[] = [
  { value: 'none', label: 'No transformation' },
  { value: 'parse_number', label: 'Parse as number (handles "$19.99")' },
  { value: 'divide', label: 'Divide by 100 (cents → dollars)' },
  { value: 'unix_seconds', label: 'Unix timestamp (seconds)' },
  { value: 'unix_ms', label: 'Unix timestamp (milliseconds)' },
  { value: 'iso8601', label: 'ISO 8601 date' },
  { value: 'uppercase', label: 'UPPERCASE' },
  { value: 'lowercase', label: 'lowercase' },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function emptyMapping(): WebhookColumnMappingInput {
  return {
    dbColumn: '',
    sourceApiField: '',
    friendlySlug: '',
    friendlyLabel: '',
    description: '',
    dataType: 'text',
  };
}

function useDebounce<T>(value: T, delay: number): T {
  const [d, setD] = useState<T>(value);
  useEffect(() => {
    const t = setTimeout(() => setD(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return d;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface DetectedArray {
  path: string;
  itemCount: number;
  sampleItemKeys: string[];
  sampleItem: unknown;
}

interface FieldMappingEditorProps {
  sample: Record<string, unknown> | null | undefined;
  dataSourceId: string;
  onSave: (mapping: SaveWebhookMappingRequest) => void;
  isLoading?: boolean;
  initialMapping?: SaveWebhookMappingRequest | null;
  isEditMode?: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const FieldMappingEditor: React.FC<FieldMappingEditorProps> = ({
  sample,
  dataSourceId,
  onSave,
  isLoading = false,
  initialMapping = null,
  isEditMode = false,
}) => {
  // State
  const [txMappings, setTxMappings] = useState<WebhookColumnMappingInput[]>(
    initialMapping?.transactionMappings?.length
      ? initialMapping.transactionMappings
      : [emptyMapping()],
  );
  const [detailMappings, setDetailMappings] = useState<WebhookColumnMappingInput[]>(
    initialMapping?.detailMappings ?? [],
  );
  const [detailsArrayPath, setDetailsArrayPath] = useState<string>(
    initialMapping?.detailsArrayPath ?? '',
  );
  const [detailsEnabled, setDetailsEnabled] = useState<boolean>(
    !!(initialMapping?.detailsArrayPath && initialMapping.detailMappings && initialMapping.detailMappings.length > 0),
  );

  const [detectedArrays, setDetectedArrays] = useState<DetectedArray[]>([]);
  const [isLoadingArrays, setIsLoadingArrays] = useState(false);

  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [previewErrors, setPreviewErrors] = useState<string[]>([]);
  const [previewWarnings, setPreviewWarnings] = useState<string[]>([]);
  const [isTestingMapping, setIsTestingMapping] = useState(false);

  // Debounced state for auto-testing
  const debouncedTx = useDebounce(txMappings, 600);
  const debouncedDetail = useDebounce(detailMappings, 600);
  const debouncedPath = useDebounce(detailsArrayPath, 600);

  // Extract all paths from the sample payload
  const getPaths = useCallback((obj: unknown, prefix = ''): string[] => {
    const paths: string[] = [];
    if (!obj || typeof obj !== 'object') return paths;
    const record = obj as Record<string, unknown>;
    for (const key in record) {
      const path = prefix ? `${prefix}.${key}` : key;
      const value = record[key];
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        paths.push(...getPaths(value, path));
      } else {
        paths.push(path);
      }
    }
    return paths;
  }, []);

  const availablePaths = getPaths(sample);

  // Detect arrays on mount
  useEffect(() => {
    (async () => {
      setIsLoadingArrays(true);
      try {
        const result = await integrationsApi.detectWebhookArrays(dataSourceId);
        if (result.success) setDetectedArrays(result.data.arrays);
      } catch { /* ignore */ } finally {
        setIsLoadingArrays(false);
      }
    })();
  }, [dataSourceId]);

  // Auto-test on debounced changes
  useEffect(() => {
    const hasTimestamp = debouncedTx.some(m => m.standardizedField === 'timestamp');
    const hasMappedColumn = debouncedTx.some(m => m.dbColumn && m.sourceApiField);
    if (!hasTimestamp || !hasMappedColumn) {
      setPreview(null);
      setPreviewErrors([]);
      return;
    }
    const req = buildRequest();
    (async () => {
      setIsTestingMapping(true);
      try {
        const res = await integrationsApi.testWebhookMapping(dataSourceId, req);
        if (res.success) {
          setPreviewErrors(res.data.errors);
          setPreviewWarnings(res.data.warnings || []);
          setPreview(res.data.preview);
        }
      } catch (e) {
        setPreviewErrors([handleApiError(e)]);
      } finally {
        setIsTestingMapping(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedTx, debouncedDetail, debouncedPath, dataSourceId]);

  // ---------------------------------------------------------------------------
  // Build request
  // ---------------------------------------------------------------------------

  const buildRequest = (): SaveWebhookMappingRequest => ({
    transactionMappings: txMappings.filter(m => m.dbColumn && (m.sourceApiField || m.transformConfig)),
    detailMappings: detailsEnabled ? detailMappings.filter(m => m.dbColumn && m.sourceApiField) : undefined,
    detailsArrayPath: detailsEnabled ? detailsArrayPath : undefined,
  });

  // ---------------------------------------------------------------------------
  // Used columns
  // ---------------------------------------------------------------------------

  const usedTxColumns = new Set(txMappings.map(m => m.dbColumn).filter(Boolean));

  // ---------------------------------------------------------------------------
  // Mutation helpers
  // ---------------------------------------------------------------------------

  const updateTxMapping = (idx: number, patch: Partial<WebhookColumnMappingInput>) => {
    setTxMappings(prev => prev.map((m, i) => {
      if (i !== idx) return m;
      const updated = { ...m, ...patch };
      // Auto-slug from label
      if (patch.friendlyLabel !== undefined && !m.friendlySlug) {
        updated.friendlySlug = slugify(patch.friendlyLabel);
      }
      return updated;
    }));
  };

  const addTxMapping = () => setTxMappings(prev => [...prev, emptyMapping()]);

  const removeTxMapping = (idx: number) => {
    setTxMappings(prev => prev.filter((_, i) => i !== idx));
  };

  // ---------------------------------------------------------------------------
  // Validation
  // ---------------------------------------------------------------------------

  const hasTimestamp = txMappings.some(m => m.standardizedField === 'timestamp');
  const hasMappedColumn = txMappings.some(m => m.dbColumn && (m.sourceApiField || m.transformConfig));

  const validateForm = (): boolean => {
    if (!hasMappedColumn) {
      toast.error('At least one field must be mapped');
      return false;
    }
    if (!hasTimestamp) {
      toast.error('One field must be designated as the Transaction Timestamp');
      return false;
    }
    for (const m of txMappings) {
      if (!m.dbColumn) continue;
      if (!m.friendlyLabel) {
        toast.error(`Label is required for ${m.dbColumn}`);
        return false;
      }
      if (!m.friendlySlug) {
        toast.error(`Slug is required for ${m.dbColumn}`);
        return false;
      }
    }
    return true;
  };

  const handleSave = () => {
    if (!validateForm()) return;
    onSave(buildRequest());
  };

  const handleTestMapping = async () => {
    setIsTestingMapping(true);
    try {
      const res = await integrationsApi.testWebhookMapping(dataSourceId, buildRequest());
      if (res.success) {
        setPreviewErrors(res.data.errors);
        setPreviewWarnings(res.data.warnings || []);
        setPreview(res.data.preview);
        if (res.data.isValid) toast.success('Mapping is valid!');
      }
    } catch (e) {
      toast.error(handleApiError(e));
    } finally {
      setIsTestingMapping(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Render a single mapping row
  // ---------------------------------------------------------------------------

  const renderMappingRow = (m: WebhookColumnMappingInput, idx: number) => {
    const isComputed = m.transformConfig?.type === 'computed';
    const isLiteral = typeof m.sourceApiField === 'string' && m.sourceApiField.startsWith('LITERAL:');

    return (
      <div
        key={idx}
        className="border border-border rounded-lg p-4 bg-card space-y-3"
      >
        {/* Row header: column slot + remove */}
        <div className="flex items-center justify-between gap-3">
          {/* Column slot dropdown */}
          <div className="flex-1">
            <label className="block text-xs font-medium text-muted-foreground mb-1">
              Column Slot
            </label>
            <select
              className="w-full border border-input p-2 rounded text-sm bg-card text-foreground focus:ring-2 focus:ring-ring"
              value={m.dbColumn}
              onChange={e => updateTxMapping(idx, { dbColumn: e.target.value })}
            >
              <option value="">Select column...</option>
              {COLUMN_GROUPS.map(g => (
                <optgroup key={g.label} label={g.label}>
                  {g.slots.map(slot => (
                    <option
                      key={slot}
                      value={slot}
                      disabled={usedTxColumns.has(slot) && m.dbColumn !== slot}
                    >
                      {slot}{usedTxColumns.has(slot) && m.dbColumn !== slot ? ' (used)' : ''}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          <button
            onClick={() => removeTxMapping(idx)}
            className="mt-5 p-2 text-destructive hover:text-destructive/80"
            title="Remove mapping"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>

        {/* Source / computed / literal */}
        <div>
          <div className="flex items-center gap-4 mb-2">
            <label className="flex items-center cursor-pointer text-sm text-muted-foreground">
              <input
                type="checkbox"
                checked={!!isComputed}
                onChange={e => {
                  if (e.target.checked) {
                    updateTxMapping(idx, {
                      transformConfig: { type: 'computed', operation: 'dateDiffSeconds', operand1: '', operand2: '' } as ComputedFieldConfig,
                      sourceApiField: '',
                    });
                  } else {
                    updateTxMapping(idx, { transformConfig: undefined, sourceApiField: '' });
                  }
                }}
                className="mr-1.5 rounded border-input text-primary focus:ring-ring"
              />
              <Calculator className="w-3.5 h-3.5 mr-1 text-primary" />
              Computed
            </label>

            {!isComputed && (
              <label className="flex items-center cursor-pointer text-sm text-muted-foreground">
                <input
                  type="checkbox"
                  checked={!!isLiteral}
                  onChange={e => {
                    updateTxMapping(idx, {
                      sourceApiField: e.target.checked ? 'LITERAL:' : '',
                    });
                  }}
                  className="mr-1.5 rounded border-input text-primary focus:ring-ring"
                />
                Literal
              </label>
            )}
          </div>

          {isComputed ? (
            <ComputedFieldBuilder
              config={m.transformConfig as ComputedFieldConfig}
              availablePaths={availablePaths}
              detectedArrays={detectedArrays.map(a => ({ path: a.path, sampleItemKeys: a.sampleItemKeys }))}
              onChange={cfg => updateTxMapping(idx, { transformConfig: cfg })}
              onCancel={() => updateTxMapping(idx, { transformConfig: undefined, sourceApiField: '' })}
            />
          ) : isLiteral ? (
            <input
              type="text"
              className="w-full border border-input p-2 rounded text-sm bg-card text-foreground focus:ring-2 focus:ring-ring"
              value={m.sourceApiField.replace('LITERAL:', '')}
              onChange={e => updateTxMapping(idx, { sourceApiField: `LITERAL:${e.target.value}` })}
              placeholder="Enter a literal value"
            />
          ) : (
            <>
              <label className="block text-xs font-medium text-muted-foreground mb-1">
                Payload Path
              </label>
              <select
                className="w-full border border-input p-2 rounded text-sm bg-card text-foreground focus:ring-2 focus:ring-ring"
                value={m.sourceApiField}
                onChange={e => updateTxMapping(idx, { sourceApiField: e.target.value })}
              >
                <option value="">Select field...</option>
                {availablePaths.map(p => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </>
          )}
        </div>

        {/* Transform (only for direct path mapping) */}
        {!isComputed && !isLiteral && m.sourceApiField && (
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">
              Transform
            </label>
            <select
              className="w-full border border-input p-2 rounded text-sm bg-card text-foreground focus:ring-2 focus:ring-ring"
              value={
                m.transformConfig && m.transformConfig.type !== 'computed'
                  ? m.transformConfig.type
                  : 'none'
              }
              onChange={e => {
                const v = e.target.value;
                if (v === 'none') {
                  updateTxMapping(idx, { transformConfig: undefined });
                } else {
                  const cfg: TransformConfig = { type: v as TransformConfig['type'] };
                  if (v === 'divide') cfg.divisor = 100;
                  updateTxMapping(idx, { transformConfig: cfg });
                }
              }}
            >
              {TRANSFORM_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        )}

        {/* Metadata: label, slug, description, dataType, unit */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">
              Label <span className="text-destructive">*</span>
            </label>
            <input
              type="text"
              className="w-full border border-input p-2 rounded text-sm bg-card text-foreground focus:ring-2 focus:ring-ring"
              value={m.friendlyLabel}
              onChange={e => {
                const label = e.target.value;
                const patch: Partial<WebhookColumnMappingInput> = { friendlyLabel: label };
                if (!m.friendlySlug || m.friendlySlug === slugify(m.friendlyLabel)) {
                  patch.friendlySlug = slugify(label);
                }
                updateTxMapping(idx, patch);
              }}
              placeholder="e.g., Order Total"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">
              Slug <span className="text-destructive">*</span>
            </label>
            <input
              type="text"
              className="w-full border border-input p-2 rounded text-sm bg-card text-foreground focus:ring-2 focus:ring-ring"
              value={m.friendlySlug}
              onChange={e => updateTxMapping(idx, { friendlySlug: e.target.value })}
              placeholder="e.g., order_total"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1">
            Description
          </label>
          <input
            type="text"
            className="w-full border border-input p-2 rounded text-sm bg-card text-foreground focus:ring-2 focus:ring-ring"
            value={m.description}
            onChange={e => updateTxMapping(idx, { description: e.target.value })}
            placeholder="Brief description of this field"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">
              Data Type
            </label>
            <select
              className="w-full border border-input p-2 rounded text-sm bg-card text-foreground focus:ring-2 focus:ring-ring"
              value={m.dataType}
              onChange={e => updateTxMapping(idx, { dataType: e.target.value as FieldDataType })}
            >
              {DATA_TYPES.map(dt => (
                <option key={dt.value} value={dt.value}>{dt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">
              Unit (optional)
            </label>
            <input
              type="text"
              className="w-full border border-input p-2 rounded text-sm bg-card text-foreground focus:ring-2 focus:ring-ring"
              value={m.unit || ''}
              onChange={e => updateTxMapping(idx, { unit: e.target.value || undefined })}
              placeholder="e.g., USD, kg, °C"
            />
          </div>
          <div className="flex flex-col justify-end">
            <label className="flex items-center cursor-pointer text-sm text-muted-foreground py-2">
              <input
                type="checkbox"
                checked={!!m.defaultMasterMetric}
                onChange={e => updateTxMapping(idx, { defaultMasterMetric: e.target.checked })}
                className="mr-1.5 rounded border-input text-primary focus:ring-ring"
              />
              Master metric
            </label>
          </div>
        </div>

        {/* Special designations */}
        <div className="flex flex-wrap gap-4 pt-1">
          <label className="flex items-center cursor-pointer text-sm text-muted-foreground">
            <input
              type="checkbox"
              checked={m.standardizedField === 'timestamp'}
              onChange={e => {
                updateTxMapping(idx, { standardizedField: e.target.checked ? 'timestamp' : undefined });
              }}
              className="mr-1.5 rounded border-input text-success focus:ring-success/30"
            />
            Transaction Timestamp
          </label>
          <label className="flex items-center cursor-pointer text-sm text-muted-foreground">
            <input
              type="checkbox"
              checked={m.standardizedField === 'externalId'}
              onChange={e => {
                updateTxMapping(idx, { standardizedField: e.target.checked ? 'externalId' : undefined });
              }}
              className="mr-1.5 rounded border-input text-info focus:ring-info/30"
            />
            External ID
          </label>
          <label className="flex items-center cursor-pointer text-sm text-muted-foreground">
            <input
              type="checkbox"
              checked={m.standardizedField === 'status'}
              onChange={e => {
                updateTxMapping(idx, { standardizedField: e.target.checked ? 'status' : undefined });
              }}
              className="mr-1.5 rounded border-input text-warn focus:ring-warn/30"
            />
            Status
          </label>
        </div>
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="space-y-6">
      {/* Header info */}
      <div className="bg-info/15 border border-info/20 rounded-lg p-4">
        <h3 className="font-semibold text-info mb-2">
          {isEditMode ? 'Edit Field Mappings' : 'Step 3: Map Webhook Fields'}
        </h3>
        <p className="text-sm text-info">
          Map each field from your webhook payload to a column slot. Provide a slug, label,
          and data type so the analysis engines can understand the data.
          {detectedArrays.length > 0 && (
            <span className="block mt-1">
              <strong>Tip:</strong> We detected {detectedArrays.length} array{detectedArrays.length > 1 ? 's' : ''} in your payload.
              Enable "Line Items" below to extract individual items.
            </span>
          )}
        </p>
      </div>

      {/* Transaction field mappings */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="font-semibold text-foreground">
            Transaction Field Mappings
          </h4>
          <Button variant="outline" size="sm" onClick={addTxMapping}>
            <Plus className="w-4 h-4 mr-1" />
            Add Field
          </Button>
        </div>

        {txMappings.map((m, i) => renderMappingRow(m, i))}
      </div>

      {/* Details / line items */}
      <div className="pt-4 border-t border-border">
        <DetailsMappingSection
          detectedArrays={detectedArrays}
          detailMappings={detailMappings}
          detailsArrayPath={detailsArrayPath}
          detailsEnabled={detailsEnabled}
          onToggle={enabled => {
            setDetailsEnabled(enabled);
            if (!enabled) {
              setDetailMappings([]);
              setDetailsArrayPath('');
            }
          }}
          onArrayPathChange={setDetailsArrayPath}
          onDetailMappingsChange={setDetailMappings}
          isLoadingArrays={isLoadingArrays}
        />
      </div>

      {/* Preview */}
      <div className="pt-4 border-t border-border">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-semibold text-foreground flex items-center space-x-2">
            <Eye className="w-5 h-5" />
            <span>Transformation Preview</span>
          </h4>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleTestMapping}
            disabled={!hasMappedColumn || isTestingMapping}
            isLoading={isTestingMapping}
          >
            <RefreshCw className="w-4 h-4 mr-1" />
            Test Mapping
          </Button>
        </div>

        {isTestingMapping && (
          <p className="text-sm text-muted-foreground animate-pulse">Testing...</p>
        )}

        {previewErrors.length > 0 && (
          <div className="bg-destructive/15 border border-destructive/20 rounded-lg p-4 mb-3">
            <ul className="list-disc pl-5 text-sm text-destructive space-y-1">
              {previewErrors.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          </div>
        )}

        {previewWarnings.length > 0 && (
          <div className="bg-warn/15 border border-warn/20 rounded-lg p-4 mb-3">
            <ul className="list-disc pl-5 text-sm text-warn space-y-1">
              {previewWarnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          </div>
        )}

        {preview && (
          <div className="bg-muted border border-border rounded-lg p-4">
            <h5 className="text-sm font-medium text-foreground mb-2">
              Transaction Data
            </h5>
            <pre className="text-xs font-mono text-foreground whitespace-pre-wrap overflow-auto max-h-60">
              {JSON.stringify(preview.transaction, null, 2)}
            </pre>
            {preview.hasDetails && preview.details?.length > 0 && (
              <>
                <h5 className="text-sm font-medium text-foreground mt-4 mb-2">
                  Detail Items ({preview.itemCount})
                </h5>
                <pre className="text-xs font-mono text-foreground whitespace-pre-wrap overflow-auto max-h-60">
                  {JSON.stringify(preview.details, null, 2)}
                </pre>
              </>
            )}
          </div>
        )}
      </div>

      {/* Validation warning */}
      {!hasTimestamp && (
        <div className="bg-warn/15 border border-warn/20 rounded-lg p-4">
          <div className="flex items-start">
            <AlertCircle className="w-5 h-5 text-warn mt-0.5 mr-3" />
            <div>
              <h4 className="font-medium text-warn mb-1">
                Timestamp Required
              </h4>
              <p className="text-sm text-warn">
                Exactly one field must be designated as "Transaction Timestamp".
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Save */}
      <div className="flex justify-end pt-6 border-t border-border">
        <Button
          onClick={handleSave}
          disabled={isLoading || !hasMappedColumn || !hasTimestamp}
          isLoading={isLoading}
        >
          {isEditMode ? 'Save Changes' : 'Save & Activate Webhook'}
        </Button>
      </div>
    </div>
  );
};

export default FieldMappingEditor;
